import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../shared/api/apiClient";
import { crmSettingsApi } from "../../settings/api/crmSettings";
import { getUserPermissions } from "../../../../src/shared/lib/auth/authHelper";
import OrderCartEditor from "./OrderCartEditor";
import ContactCustomerSelect from "./ContactCustomerSelect";
import "./ProductsPage.css";

const emptyForm = {
  customer_name: "",
  count: "",
  length: "",
  width: "",
  height: "",
  status: "pending",
  price_per_piece: "",
  total_price: "",
  description: "",
  category_id: "",
  sub_category_id: "",
  custom_field_values: {},
};

const WAREHOUSE_PRESET_KEYS = new Set([
  "warehouse",
  "wood",
  "wood_warehouse",
  "timber",
  "lumber",
  "manufacturing",
  "warehouse_shop",
  "wood_sales",
  "woodworking",
  "wood_sales_woodworking",
  "wood_sales_and_woodworking",
  "wood_sales/woodworking",
]);

const WAREHOUSE_CATEGORY_HINTS = [
  "wood",
  "timber",
  "lumber",
  "oak",
  "pine",
  "warehouse",
  "stock",
];

function normalizePresetKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function getPresetKeyFromSettings(settings) {
  const settingsJson =
    settings?.settings_json && typeof settings.settings_json === "object"
      ? settings.settings_json
      : {};

  const businessPreset = settingsJson.business_preset;
  const industry = settingsJson.industry;

  if (typeof businessPreset === "string") return normalizePresetKey(businessPreset);
  if (businessPreset?.key) return normalizePresetKey(businessPreset.key);
  if (businessPreset?.preset_key) return normalizePresetKey(businessPreset.preset_key);

  if (typeof industry === "string") return normalizePresetKey(industry);
  if (industry?.key) return normalizePresetKey(industry.key);

  return "";
}


function presetLooksLikeWarehouse(value) {
  const key = normalizePresetKey(value);

  if (WAREHOUSE_PRESET_KEYS.has(key)) return true;

  return (
    key.includes("wood") ||
    key.includes("timber") ||
    key.includes("lumber") ||
    key.includes("warehouse") ||
    key.includes("woodworking") ||
    key.includes("wood_sales")
  );
}

function Products() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [productCustomFields, setProductCustomFields] = useState([]);
  const [formData, setFormData] = useState(emptyForm);

  const [crmPresetKey, setCrmPresetKey] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [currentProductId, setCurrentProductId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [statusFilter, setStatusFilter] = useState("all");
  const [originFilter, setOriginFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [selectedCommerceOrder, setSelectedCommerceOrder] = useState(null);
  const [commerceSaving, setCommerceSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [creationMode, setCreationMode] = useState(() => {
    const storedMode = window.localStorage.getItem("orders_creation_mode");
    return storedMode === "cart" ? "cart" : "quick";
  });
  const [editingCartOrder, setEditingCartOrder] = useState(null);
  const [cartDirty, setCartDirty] = useState(false);
  const [cartEditorVersion, setCartEditorVersion] = useState(0);

  const permissions = getUserPermissions() || [];
  const canCreate = permissions.includes("products.create");
  const canUpdate = permissions.includes("products.update");
  const canDelete = permissions.includes("products.delete");
  const canShowForm = canCreate || canUpdate;

  const selectedCategory = useMemo(() => {
    return (
      categories.find(
        (category) => String(category.id) === String(formData.category_id)
      ) || null
    );
  }, [categories, formData.category_id]);

  const categoryLooksLikeWarehouse = (category) => {
    const text = `${category?.name || ""} ${category?.key || ""}`.toLowerCase();
    return WAREHOUSE_CATEGORY_HINTS.some((hint) => text.includes(hint));
  };

  const isWarehousePreset = useMemo(() => {
    if (!settingsLoaded) return false;
    return presetLooksLikeWarehouse(crmPresetKey);
  }, [crmPresetKey, settingsLoaded]);

  const selectedCategoryUsesWarehouseFields = useMemo(() => {
    // Wood tenant must keep the original static warehouse behavior.
    // This preserves existing client stock/order flow.
    if (isWarehousePreset) return true;

    if (!formData.category_id) return false;
    return categoryLooksLikeWarehouse(selectedCategory);
  }, [formData.category_id, isWarehousePreset, selectedCategory]);

  const visibleProductCustomFields = useMemo(
    () =>
      selectedCategoryUsesWarehouseFields
        ? []
        : productCustomFields.filter((field) => field.is_active !== false),
    [selectedCategoryUsesWarehouseFields, productCustomFields]
  );

  const selectedSubCategory = useMemo(() => {
    return (
      subCategories.find(
        (sub) => String(sub.id) === String(formData.sub_category_id)
      ) || null
    );
  }, [subCategories, formData.sub_category_id]);

  const tableProductCustomFields = useMemo(
    () => visibleProductCustomFields.filter((field) => field.is_visible_in_table),
    [visibleProductCustomFields]
  );

  useEffect(() => {
    fetchCrmSettings();
    fetchProducts();
    fetchCategories();
    fetchProductCustomFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCrmSettings = async () => {
    try {
      const settings = await crmSettingsApi.get();
      setCrmPresetKey(getPresetKeyFromSettings(settings));
      setCurrency(
        settings?.currency || settings?.settings_json?.currency || "EUR"
      );
    } catch (error) {
      console.error("Error fetching CRM settings", error);
      setCrmPresetKey("");
    } finally {
      setSettingsLoaded(true);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/auth/category/all");
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(t("error_fetching_categories"), error);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    if (!categoryId) {
      setSubCategories([]);
      return [];
    }

    try {
      const response = await api.get(
        `/auth/subcategories/by-category-id/${categoryId}`
      );
      const rows = Array.isArray(response.data) ? response.data : [];
      setSubCategories(rows);
      return rows;
    } catch (error) {
      console.error(t("error_fetching_subcategories"), error);
      setSubCategories([]);
      return [];
    }
  };

  const fetchProductCustomFields = async () => {
    try {
      const response = await api.get("/custom-fields/product");
      setProductCustomFields(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching product custom fields", error);
      setProductCustomFields([]);
    }
  };

  const normalizeOrder = (order) => {
    const items = Array.isArray(order?.items) ? order.items : [];
    const firstItem = items[0] || {};

    return {
      ...firstItem,
      ...order,
      order_id: order.id,
      order_ui_id: order.ui_id,
      items,
      item_count: Number(order.item_count ?? items.length),
      total_quantity: Number(
        order.total_quantity ??
          items.reduce((sum, item) => sum + Number(item.count || 0), 0)
      ),
      count: Number(
        order.total_quantity ??
          items.reduce((sum, item) => sum + Number(item.count || 0), 0)
      ),
    };
  };

  const groupLegacyProducts = (rows) => {
    const grouped = new Map();

    rows.forEach((product) => {
      const orderId = product.order_id || product.id;
      if (!grouped.has(orderId)) {
        grouped.set(orderId, {
          id: orderId,
          ui_id: product.order_ui_id || product.ui_id || orderId,
          customer_name: product.customer_name,
          description: product.description,
          status: product.status,
          total_price: 0,
          created_at: product.created_at,
          last_update: product.last_update,
          items: [],
        });
      }

      const order = grouped.get(orderId);
      order.items.push(product);
      order.total_price += Number(product.total_price || 0);
      if (product.last_update && (!order.last_update || product.last_update > order.last_update)) {
        order.last_update = product.last_update;
      }
    });

    return Array.from(grouped.values()).map(normalizeOrder);
  };

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/auth/product/all", {
        params: { skip: 0, limit: 100 },
      });
      const productRows = Array.isArray(response.data) ? response.data : [];
      setProducts(productRows);

      try {
        const ordersResponse = await api.get("/auth/orders/all", {
          params: { skip: 0, limit: 100 },
        });
        const orderRows = Array.isArray(ordersResponse.data)
          ? ordersResponse.data.map(normalizeOrder)
          : [];
        setOrders(orderRows);
      } catch (ordersError) {
        // Allows the frontend to keep showing legacy rows during a rolling deploy.
        console.warn("Orders endpoint is not available yet; using legacy rows", ordersError);
        setOrders(groupLegacyProducts(productRows));
      }
    } catch (error) {
      console.error(t("error_fetching_products"), error);
      setError(t("error_fetching_products"));
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = (clearFeedback = true) => {
    setFormData(emptyForm);
    setIsEditing(false);
    setCurrentProductId(null);
    setSubCategories([]);
    if (clearFeedback) {
      setError("");
      setSuccess("");
    }
  };

  const handleCategoryChange = (event) => {
    const categoryId = event.target.value;

    setFormData((prev) => ({
      ...prev,
      category_id: categoryId,
      sub_category_id: "",
      count: "",
      length: "",
      width: "",
      height: "",
      price_per_piece: "",
      total_price: "",
    }));

    fetchSubCategories(categoryId);
  };

  const resolveTechnicalWarehouseFields = async () => {
    const categoryId = parseInt(formData.category_id, 10);
    const subCategoryId = parseInt(formData.sub_category_id, 10);

    if (!categoryId) {
      throw new Error(t("select_category"));
    }

    if (!subCategoryId) {
      throw new Error(t("select_sub_category"));
    }

    const selectedSubCategory = subCategories.find(
      (sub) => parseInt(sub.id, 10) === subCategoryId
    );

    if (!selectedSubCategory) {
      throw new Error(t("error_invalid_subcategory"));
    }

    const count = parseInt(formData.count || 0, 10);
    if (!count || count <= 0) {
      throw new Error(t("quantity_required", { defaultValue: "Quantity is required" }));
    }

    if (selectedCategoryUsesWarehouseFields) {
      return {
        category_id: categoryId,
        sub_category_id: subCategoryId,
        count,
        length: parseInt(formData.length || selectedSubCategory.length || 1, 10),
        width: parseInt(formData.width || selectedSubCategory.width || 1, 10),
        height: parseInt(formData.height || selectedSubCategory.height || 1, 10),
        selectedSubCategory,
      };
    }

    return {
      category_id: categoryId,
      sub_category_id: subCategoryId,
      count,
      length: parseInt(selectedSubCategory.length || 1, 10),
      width: parseInt(selectedSubCategory.width || 1, 10),
      height: parseInt(selectedSubCategory.height || 1, 10),
      selectedSubCategory,
    };
  };

  const validateWarehouseStock = ({ selectedSubCategory, count }) => {
    if (!selectedCategoryUsesWarehouseFields) return;

    const availableCount =
      Number(selectedSubCategory.count || 0) -
      Number(selectedSubCategory.booked || 0);

    let previousCount = 0;

    if (isEditing && currentProductId) {
      const existingProduct = products.find((prod) => prod.id === currentProductId);
      if (existingProduct) previousCount = parseInt(existingProduct.count, 10);
    }

    if (!isEditing && count > availableCount) {
      throw new Error(t("error_count_exceeds_available"));
    }

    if (isEditing && count > previousCount) {
      throw new Error(t("error_edit_count_increase_forbidden"));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    setIsSaving(true);

    try {
      if (!formData.customer_name.trim()) {
        throw new Error(t("cart_customer_required"));
      }

      const technicalFields = await resolveTechnicalWarehouseFields();

      validateWarehouseStock(technicalFields);

      const updatedData = {
        customer_name: formData.customer_name,
        count: technicalFields.count,
        length: technicalFields.length,
        width: technicalFields.width,
        height: technicalFields.height,
        status: formData.status,
        total_price: formData.total_price || 0,
        description: formData.description,
        category_id: technicalFields.category_id,
        sub_category_id: technicalFields.sub_category_id,
        custom_field_values: formData.custom_field_values || {},
      };

      if (isEditing && currentProductId) {
        await api.put(`/auth/product/edit/${currentProductId}`, updatedData);
        setSuccess(t("order_updated_successfully"));
      } else {
        await api.post("/auth/product/add", updatedData);
        setSuccess(t("order_created_successfully"));
      }

      resetForm(false);
      await fetchProducts();
    } catch (error) {
      setError(error?.message || t("error_saving_product"));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "sub_category_id") {
      const selectedSubCategory = subCategories.find(
        (sub) => sub.id.toString() === value
      );

      setFormData((prev) => {
        const pricePerPiece = selectedSubCategory
          ? Number(selectedSubCategory.price_per_piece) || 0
          : 0;
        const countValue = Math.max(0, Number(prev.count || 0));

        return {
          ...prev,
          sub_category_id: value,
          length: selectedSubCategory ? selectedSubCategory.length : "",
          width: selectedSubCategory ? selectedSubCategory.width : "",
          height: selectedSubCategory ? selectedSubCategory.height : "",
          price_per_piece: pricePerPiece,
          total_price: countValue > 0 ? countValue * pricePerPiece : "",
        };
      });

      return;
    }

    setFormData((prev) => {
      const updatedData = { ...prev, [name]: value };

      if (name === "count") {
        const countValue = Math.max(0, Number(value));
        const found = subCategories.find(
          (sub) => sub.id.toString() === String(prev.sub_category_id)
        );

        const pricePerPiece = found ? Number(found.price_per_piece) || 0 : 0;
        updatedData.price_per_piece = pricePerPiece;
        updatedData.total_price = countValue * pricePerPiece;
      }

      return updatedData;
    });
  };

  const handleCustomFieldChange = (fieldKey, value) => {
    setFormData((prev) => ({
      ...prev,
      custom_field_values: {
        ...(prev.custom_field_values || {}),
        [fieldKey]: value,
      },
    }));
  };

  const getCustomFieldLabel = (field) => {
    const labelKey = field?.label || field?.label_key || field?.key || "";
    const fallback =
      field?.options_json?.fallback_label ||
      field?.fallback_label ||
      field?.key ||
      labelKey;

    return t(labelKey, { defaultValue: fallback });
  };

  const getCustomFieldValue = (field) => {
    const values = formData.custom_field_values || {};
    const value = values[field.key];

    if (value === undefined || value === null || value === "") {
      return field.default_value ?? "";
    }

    return value;
  };

  const renderCustomFieldInput = (field) => {
    const value = getCustomFieldValue(field);
    const label = getCustomFieldLabel(field);
    const isRequired = !!field.is_required;

    if (field.data_type === "boolean") {
      return (
        <label className="productsCustomCheckbox">
          <input
            type="checkbox"
            checked={value === true || value === "true" || value === "1"}
            onChange={(event) =>
              handleCustomFieldChange(field.key, event.target.checked)
            }
          />
          <span>{label}</span>
        </label>
      );
    }

    if (field.data_type === "select") {
      const options = Array.isArray(field.options_json?.options)
        ? field.options_json.options
        : [];

      return (
        <select
          value={value}
          required={isRequired}
          onChange={(event) => handleCustomFieldChange(field.key, event.target.value)}
        >
          <option value="">{t("select", { defaultValue: "Select" })}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {t(String(option), { defaultValue: String(option) })}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={
          field.data_type === "number"
            ? "number"
            : field.data_type === "date"
              ? "date"
              : "text"
        }
        value={value}
        required={isRequired}
        onChange={(event) => handleCustomFieldChange(field.key, event.target.value)}
      />
    );
  };

  const getCustomFieldDisplayValue = (product, field) => {
    const values =
      product.custom_field_values ||
      product.custom_field_values_map ||
      {};
    const value = values[field.key];

    if (value === undefined || value === null || value === "") {
      return field.default_value ?? "-";
    }

    if (field.data_type === "boolean") {
      return value === true || value === "true" || value === "1"
        ? t("yes", { defaultValue: "Yes" })
        : t("no", { defaultValue: "No" });
    }

    return String(value);
  };

  const handleEdit = async (product) => {
    setIsEditing(true);
    setCurrentProductId(product.id);
    setError("");
    setSuccess("");

    setFormData({
      id: product.id,
      customer_name: product.customer_name || "",
      count: product.count || "",
      length: product.length || "",
      width: product.width || "",
      height: product.height || "",
      status: product.status || "pending",
      price_per_piece: product.price_per_piece || product.sub_category_obj?.price_per_piece || "",
      total_price: product.total_price || "",
      description: product.description || "",
      category_id: product.category_id || "",
      sub_category_id: product.sub_category_id || "",
      custom_field_values:
        product.custom_field_values ||
        product.custom_field_values_map ||
        {},
      created_at: product.created_at,
      last_update: product.last_update,
    });

    if (product.category_id) {
      await fetchSubCategories(product.category_id);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(t("confirm_delete_order"))) return;

    setError("");
    setSuccess("");

    try {
      await api.delete(`/auth/orders/${order.order_id || order.id}`);
      setSuccess(t("order_deleted_successfully"));
      await fetchProducts();
    } catch (error) {
      setError(error.response?.data?.detail || t("error_deleting_product"));
      console.error(error);
    }
  };

  const handleCreationModeChange = (nextMode) => {
    if (nextMode === creationMode) return;
    if (cartDirty && !window.confirm(t("confirm_leave_cart"))) return;

    resetForm();
    setEditingCartOrder(null);
    setCartDirty(false);
    setCreationMode(nextMode);
    window.localStorage.setItem("orders_creation_mode", nextMode);
  };

  const openOrderForEdit = async (order) => {
    const isSingleItem = Number(order.item_count || order.items?.length || 0) <= 1;

    if (creationMode === "quick" && isSingleItem) {
      const item = order.items?.[0] || order;
      await handleEdit({
        ...item,
        customer_name: order.customer_name,
        description: order.description,
        status: order.status,
        created_at: order.created_at,
        last_update: order.last_update,
      });
      return;
    }

    if (cartDirty && !window.confirm(t("confirm_leave_cart"))) return;

    setError("");
    setSuccess("");
    try {
      const response = await api.get(`/auth/orders/${order.order_id || order.id}`);
      setCreationMode("cart");
      window.localStorage.setItem("orders_creation_mode", "cart");
      setEditingCartOrder(response.data);
      setCartDirty(false);
      window.requestAnimationFrame(() => {
        document
          .getElementById("order-editor")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || t("cart_order_load_failed"));
    }
  };

  const handleCartSaved = async (action) => {
    setEditingCartOrder(null);
    setCartDirty(false);
    setCartEditorVersion((version) => version + 1);
    setSuccess(
      action === "updated"
        ? t("order_updated_successfully")
        : t("order_created_successfully")
    );
    await fetchProducts();
  };

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return orders.filter((product) => {
      const matchesStatus =
        statusFilter === "all" || product.status === statusFilter;
      const normalizedPaymentStatus = String(product.payment_status || "").toUpperCase();
      const matchesOrigin =
        originFilter === "all" ||
        (originFilter === "shop" ? product.origin === "velshop" : product.origin !== "velshop");
      const matchesPayment =
        paymentFilter === "all" || normalizedPaymentStatus === paymentFilter;
      const itemMatchesQuery = (product.items || []).some((item) =>
        [
          item.length,
          item.width,
          item.height,
          item.category_id,
          item.sub_category_id,
          item.category_obj?.name,
          item.category_obj?.fallback_name,
          item.sub_category_obj?.name,
          item.sub_category_obj?.fallback_name,
          ...Object.values(item.custom_field_values || item.custom_field_values_map || {}),
        ]
          .join(" ")
          .toLowerCase()
          .includes(query)
      );
      const matchesQuery =
        !query || (
        product.customer_name?.toLowerCase().includes(query) ||
        String(product.ui_id || product.id || "").toLowerCase().includes(query) ||
        product.status?.toLowerCase().includes(query) ||
        String(product.total_price || "").toLowerCase().includes(query) ||
        String(product.description || "").toLowerCase().includes(query) ||
        itemMatchesQuery
      );

      return matchesStatus && matchesOrigin && matchesPayment && matchesQuery;
    });
  }, [orders, originFilter, paymentFilter, searchQuery, statusFilter]);

  const sortData = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";

    setSortConfig({ key, direction });

    const valueForSort = (item) => {
      if (key === "category")
        return t(item.category_obj?.name || "", {
          defaultValue: item.category_obj?.fallback_name || "",
        });

      if (key === "sub_category")
        return t(item.sub_category_obj?.name || "", {
          defaultValue: item.sub_category_obj?.fallback_name || "",
        });
      if (key === "product_id") return item.ui_id || item.id || "";
      if (key === "count") return item.total_quantity || item.count || 0;
      return item[key];
    };

    const sortedProducts = [...orders].sort((a, b) => {
      const av = valueForSort(a);
      const bv = valueForSort(b);

      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setOrders(sortedProducts);
  };

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage) || 1;

  const paginate = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handleProductsPerPageChange = (event) => {
    setProductsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleOriginFilterChange = (value) => {
    setOriginFilter(value);
    setCurrentPage(1);
  };

  const handlePaymentFilterChange = (value) => {
    setPaymentFilter(value);
    setCurrentPage(1);
  };

  const updateCommerceState = async (order, changes) => {
    if (!order?.id || commerceSaving) return;
    setCommerceSaving(true);
    setError("");
    try {
      const response = await api.patch(`/auth/orders/${order.id}/commerce`, changes);
      const updated = normalizeOrder(response.data);
      setOrders((previous) => previous.map((row) => (row.id === updated.id ? updated : row)));
      setSelectedCommerceOrder(updated);
      setSuccess(t("commerce_status_updated"));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || t("commerce_status_update_failed"));
    } finally {
      setCommerceSaving(false);
    }
  };

  const getCategoryDisplayName = (product) =>
    t(product?.category_obj?.name || "", {
      defaultValue:
        product?.category_obj?.fallback_name ||
        product?.category_obj?.name ||
        t("n_a"),
    });

  const getSubcategoryDisplayName = (product) =>
    t(product?.sub_category_obj?.name || "", {
      defaultValue:
        product?.sub_category_obj?.fallback_name ||
        product?.sub_category_obj?.name ||
        t("n_a"),
    });

  const formatMoney = (value) => {
    const amount = Number(value || 0);
    const locale = i18n.resolvedLanguage || i18n.language || "en";

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EUR",
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2)} ${currency || "EUR"}`;
    }
  };

  const selectedStock = useMemo(() => {
    if (!selectedSubCategory) return null;

    const stock = Number(selectedSubCategory.count || 0);
    const booked = Math.max(Number(selectedSubCategory.booked || 0), 0);

    return {
      stock,
      booked,
      available: Math.max(stock - booked, 0),
    };
  }, [selectedSubCategory]);

  const orderStats = useMemo(() => {
    const byStatus = orders.reduce(
      (acc, product) => {
        if (Object.prototype.hasOwnProperty.call(acc, product.status)) {
          acc[product.status] += 1;
        }
        return acc;
      },
      { pending: 0, completed: 0, canceled: 0 }
    );

    const activeValue = orders
      .filter((product) => product.status !== "canceled")
      .reduce((sum, product) => sum + Number(product.total_price || 0), 0);
    const shopOrders = orders.filter((product) => product.origin === "velshop");
    const paidOrders = shopOrders.filter((product) => String(product.payment_status || "").toUpperCase() === "PAID");
    const paidValue = paidOrders.reduce((sum, product) => sum + Number(product.total_price || 0), 0);
    const awaitingPayment = shopOrders.filter((product) => ["", "PENDING", "UNKNOWN"].includes(String(product.payment_status || "").toUpperCase())).length;
    const paymentProblems = shopOrders.filter((product) => ["FAILED", "CANCELED"].includes(String(product.payment_status || "").toUpperCase())).length;
    const refundRequests = shopOrders.filter((product) => ["REQUESTED", "PROCESSING"].includes(String(product.refund_status || "").toUpperCase())).length;

    return {
      total: orders.length,
      activeValue,
      shopTotal: shopOrders.length,
      paidValue,
      awaitingPayment,
      paymentProblems,
      refundRequests,
      ...byStatus,
    };
  }, [orders]);

  const startNewOrder = () => {
    if (creationMode === "cart" && cartDirty && !window.confirm(t("confirm_clear_cart"))) {
      return;
    }
    resetForm();
    setEditingCartOrder(null);
    setCartDirty(false);
    setCartEditorVersion((version) => version + 1);
    window.requestAnimationFrame(() => {
      document
        .getElementById("order-editor")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const showingText = t("showing_entries", {
    start:
      filteredProducts.length === 0
        ? 0
        : (currentPage - 1) * productsPerPage + 1,
    end: Math.min(currentPage * productsPerPage, filteredProducts.length),
    total: filteredProducts.length,
  });

  const sortArrow = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return t("n_a");
    return new Date(dateValue).toLocaleString(
      i18n.resolvedLanguage || i18n.language || undefined
    );
  };

  return (
    <div className="productsPage">
      <section className="productsTopBar">
        <div className="productsTopBarLeft">
          <h1 className="productsTopBarTitle">{t("orders")}</h1>
          <div className="productsTopBarSubtitle">
            {t("orders_list_hint", {
              defaultValue: "Create, search and manage customer orders.",
            })}
          </div>
        </div>

        <div className="productsTopActions">
          <button
            className="productsSecondaryBtn"
            type="button"
            onClick={() => navigate("/menu/settings/products")}
          >
            <i className="lni lni-gear-1" aria-hidden="true"></i>
            {t("manage_product_catalog")}
          </button>
          {canCreate && (
            <button className="productsPrimaryBtn" type="button" onClick={startNewOrder}>
              <i className="lni lni-plus" aria-hidden="true"></i>
              {t("create_order")}
            </button>
          )}
        </div>
      </section>

      <section className="commerceControlCenter">
        <div className="commerceControlHeader">
          <div>
            <span className="commerceEyebrow">{t("shop_operations")}</span>
            <h2>{t("orders_payments_control_center")}</h2>
            <p>{t("orders_payments_control_center_hint")}</p>
          </div>
          <div className="commerceControlFilters">
            <select value={originFilter} onChange={(event) => handleOriginFilterChange(event.target.value)}>
              <option value="all">{t("all_order_sources")}</option>
              <option value="shop">{t("shop_orders")}</option>
              <option value="crm">{t("crm_orders")}</option>
            </select>
            <select value={paymentFilter} onChange={(event) => handlePaymentFilterChange(event.target.value)}>
              <option value="all">{t("all_payment_statuses")}</option>
              <option value="PENDING">{t("payment_pending")}</option>
              <option value="PAID">{t("payment_paid")}</option>
              <option value="FAILED">{t("payment_failed")}</option>
              <option value="REFUNDED">{t("payment_refunded")}</option>
            </select>
          </div>
        </div>
        <div className="commerceKpis">
          <div><span>{t("shop_orders")}</span><strong>{orderStats.shopTotal}</strong><small>{t("from_storefront")}</small></div>
          <div className="isSuccess"><span>{t("paid_turnover")}</span><strong>{formatMoney(orderStats.paidValue)}</strong><small>{t("confirmed_payments")}</small></div>
          <div className="isWarning"><span>{t("awaiting_payment")}</span><strong>{orderStats.awaitingPayment}</strong><small>{t("requires_follow_up")}</small></div>
          <div className="isDanger"><span>{t("payment_issues")}</span><strong>{orderStats.paymentProblems}</strong><small>{t("failed_or_canceled")}</small></div>
          <div className="isInfo"><span>{t("refund_requests")}</span><strong>{orderStats.refundRequests}</strong><small>{t("open_refund_requests")}</small></div>
        </div>
      </section>

      <section className="productsMetrics" aria-label={t("orders_overview")}>
        <button
          type="button"
          className={`productsMetric ${statusFilter === "all" ? "isActive" : ""}`}
          onClick={() => handleStatusFilterChange("all")}
        >
          <span className="productsMetricLabel">{t("all_orders")}</span>
          <strong>{orderStats.total}</strong>
          <small>{t("orders_in_system")}</small>
        </button>
        <button
          type="button"
          className={`productsMetric productsMetricPending ${statusFilter === "pending" ? "isActive" : ""}`}
          onClick={() => handleStatusFilterChange("pending")}
        >
          <span className="productsMetricLabel">{t("pending")}</span>
          <strong>{orderStats.pending}</strong>
          <small>{t("orders_need_attention")}</small>
        </button>
        <button
          type="button"
          className={`productsMetric productsMetricCompleted ${statusFilter === "completed" ? "isActive" : ""}`}
          onClick={() => handleStatusFilterChange("completed")}
        >
          <span className="productsMetricLabel">{t("completed")}</span>
          <strong>{orderStats.completed}</strong>
          <small>{t("orders_completed_hint")}</small>
        </button>
        <button
          type="button"
          className={`productsMetric productsMetricCanceled ${statusFilter === "canceled" ? "isActive" : ""}`}
          onClick={() => handleStatusFilterChange("canceled")}
        >
          <span className="productsMetricLabel">{t("canceled")}</span>
          <strong>{orderStats.canceled}</strong>
          <small>{t("orders_canceled_hint")}</small>
        </button>
        <div className="productsMetric productsMetricValue">
          <span className="productsMetricLabel">{t("active_order_value")}</span>
          <strong>{formatMoney(orderStats.activeValue)}</strong>
          <small>{t("excluding_canceled_orders")}</small>
        </div>
      </section>

      {canShowForm && (
        <section className="productsModeBar" aria-labelledby="order-mode-title">
          <div className="productsModeIntro">
            <span className="productsModeIcon" aria-hidden="true">
              <i className="lni lni-layout-9"></i>
            </span>
            <div>
              <strong id="order-mode-title">{t("order_mode_title")}</strong>
              <span>{t("order_mode_description")}</span>
            </div>
          </div>

          <div className="productsModeSwitch" role="radiogroup" aria-label={t("order_mode_title")}>
            <button
              type="button"
              role="radio"
              aria-checked={creationMode === "quick"}
              className={creationMode === "quick" ? "isActive" : ""}
              onClick={() => handleCreationModeChange("quick")}
            >
              <i className="lni lni-bolt-2" aria-hidden="true"></i>
              <span>
                <strong>{t("quick_order")}</strong>
                <small>{t("quick_order_hint")}</small>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={creationMode === "cart"}
              className={creationMode === "cart" ? "isActive" : ""}
              onClick={() => handleCreationModeChange("cart")}
            >
              <i className="lni lni-cart-1" aria-hidden="true"></i>
              <span>
                <strong>{t("cart_order_mode")}</strong>
                <small>{t("cart_order_mode_hint")}</small>
              </span>
            </button>
          </div>
        </section>
      )}

      {canShowForm && creationMode === "quick" && (
        <section id="order-editor" className="productsCard productsFormCard">
          <div className="productsCardHeader">
            <div className="productsCardTitle">
              <h3>{isEditing ? t("edit_order") : t("new_order")}</h3>
              <span>
                {isEditing ? t("edit_order_hint") : t("orders_form_hint")}
              </span>
            </div>

            <div className="productsHeaderMeta">
              {isEditing && (
                <span className="productsEditBadge">
                  {t("order_number", { number: currentProductId })}
                </span>
              )}
              <button
                className="productsIconBtn"
                type="button"
                onClick={() => navigate("/menu/settings/products")}
                title={t("manage_product_catalog")}
                aria-label={t("manage_product_catalog")}
              >
                <i className="lni lni-gear-1" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div className="productsCardBody">
            {categories.length === 0 && (
              <div className="productsCatalogNotice">
                <div className="productsCatalogNoticeIcon">
                  <i className="lni lni-box-closed" aria-hidden="true"></i>
                </div>
                <div>
                  <strong>{t("catalog_setup_required")}</strong>
                  <span>{t("catalog_setup_required_hint")}</span>
                </div>
                <button
                  type="button"
                  className="productsSecondaryBtn"
                  onClick={() => navigate("/menu/settings/products")}
                >
                  {t("configure_catalog")}
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="productsGrid">
                <div className="productsFormSectionTitle span12">
                  <span>1</span>
                  <div>
                    <strong>{t("select_order_item")}</strong>
                    <small>{t("select_order_item_hint")}</small>
                  </div>
                </div>
                <>
                  <div className="productsField span3">
                    <label>{t("category")}</label>
                      <select
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleCategoryChange}
                        required
                      >
                        <option value="">{t("select_category")}</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {t(cat.name, { defaultValue: cat.fallback_name || cat.name })}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="productsField span3">
                      <label>{t("sub_category")}</label>
                      <select
                        name="sub_category_id"
                        value={formData.sub_category_id}
                        onChange={handleChange}
                        required
                        disabled={!formData.category_id}
                        title={!formData.category_id ? t("select_category_first") : ""}
                      >
                        <option value="">{t("select_sub_category")}</option>
                        {subCategories.map((subCategory) => (
                          <option key={subCategory.id} value={subCategory.id}>
                            {`${t(subCategory.name, {
                              defaultValue: subCategory.fallback_name || subCategory.name,
                            })} (${subCategory.count} / ${subCategory.booked})`}
                          </option>
                        ))}
                      </select>
                    </div>
                </>

                {selectedSubCategory && (
                  <div className="productsSelectionCard span12">
                    <div className="productsSelectionName">
                      <span className="productsSelectionIcon">
                        <i className="lni lni-box-closed" aria-hidden="true"></i>
                      </span>
                      <div>
                        <small>{t("selected_item")}</small>
                        <strong>
                          {t(selectedSubCategory.name, {
                            defaultValue:
                              selectedSubCategory.fallback_name || selectedSubCategory.name,
                          })}
                        </strong>
                      </div>
                    </div>

                    <div className="productsSelectionStats">
                      <div>
                        <span>{t("unit_price")}</span>
                        <strong>{formatMoney(formData.price_per_piece)}</strong>
                      </div>
                      {selectedCategoryUsesWarehouseFields && selectedStock && (
                        <>
                          <div>
                            <span>{t("in_stock")}</span>
                            <strong>{selectedStock.stock}</strong>
                          </div>
                          <div>
                            <span>{t("booked")}</span>
                            <strong>{selectedStock.booked}</strong>
                          </div>
                          <div className={selectedStock.available === 0 ? "isEmpty" : "isAvailable"}>
                            <span>{t("available")}</span>
                            <strong>{selectedStock.available}</strong>
                          </div>
                        </>
                      )}
                      {selectedCategoryUsesWarehouseFields && (
                        <div>
                          <span>{t("size")}</span>
                          <strong>
                            {formData.length || "—"} × {formData.width || "—"} × {formData.height || "—"}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="productsFormSectionTitle span12">
                  <span>2</span>
                  <div>
                    <strong>{t("order_details")}</strong>
                    <small>{t("order_details_hint")}</small>
                  </div>
                </div>

                <div className="productsField span3">
                  <label>{t("customer_name")}</label>
                  <ContactCustomerSelect
                    value={formData.customer_name}
                    onChange={(customerName) =>
                      setFormData((previous) => ({
                        ...previous,
                        customer_name: customerName,
                      }))
                    }
                    disabled={isSaving}
                  />
                </div>

                <div className="productsField span3">
                  <label>{t("count")}</label>
                  <input
                    type="number"
                    min="1"
                    max={
                      !isEditing && selectedCategoryUsesWarehouseFields && selectedStock
                        ? selectedStock.available
                        : undefined
                    }
                    name="count"
                    value={formData.count}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="productsField span3">
                  <label>{t("price_per_piece", { defaultValue: "Price per piece" })}</label>
                  <input
                    type="number"
                    name="price_per_piece"
                    value={formData.price_per_piece || ""}
                    readOnly
                  />
                </div>

                {selectedCategoryUsesWarehouseFields && (
                  <>
                    <div className="productsField span3">
                      <label>{t("length")}</label>
                      <input type="text" value={formData.length || ""} readOnly />
                    </div>

                    <div className="productsField span3">
                      <label>{t("width")}</label>
                      <input type="number" value={formData.width || ""} readOnly />
                    </div>

                    <div className="productsField span3">
                      <label>{t("height")}</label>
                      <input type="number" value={formData.height || ""} readOnly />
                    </div>
                  </>
                )}

                <div className="productsField span3">
                  <label>{t("total_price")}</label>
                  <input
                    type="number"
                    name="total_price"
                    value={formData.total_price}
                    readOnly
                    required
                  />
                </div>

                <div className="productsField span6">
                  <label>{t("order_note")}</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder={t("order_note_placeholder")}
                    required
                  ></textarea>
                </div>

                {visibleProductCustomFields.map((field) => (
                  <div key={field.id || field.key} className="productsField span3">
                    <label>
                      {getCustomFieldLabel(field)}
                      {field.is_required ? " *" : ""}
                    </label>
                    {renderCustomFieldInput(field)}
                  </div>
                ))}

                {isEditing && (
                  <div className="productsField span3">
                    <label>{t("status")}</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      required
                    >
                      <option value="pending">{t("pending")}</option>
                      <option value="completed">{t("completed")}</option>
                      <option value="canceled">{t("canceled")}</option>
                    </select>
                  </div>
                )}

                {formData.category_id && !selectedCategoryUsesWarehouseFields && visibleProductCustomFields.length === 0 && (
                  <div className="productsEmptyState span12">
                    {t("product_dynamic_no_fields", {
                      defaultValue:
                        "No product fields configured yet. Add fields in CRM Settings → Product Fields.",
                    })}
                  </div>
                )}

                <div className="productsOrderSummary span12">
                  <div className="productsOrderSummaryText">
                    <small>{t("order_total")}</small>
                    <strong>{formatMoney(formData.total_price)}</strong>
                    <span>{t("order_total_calculated_hint")}</span>
                  </div>
                  <div className="productsOrderSummaryFormula">
                    <span>{formatMoney(formData.price_per_piece)}</span>
                    <i>×</i>
                    <span>{Number(formData.count || 0)}</span>
                    <i>=</i>
                    <strong>{formatMoney(formData.total_price)}</strong>
                  </div>
                </div>

                <div className="productsActions span12">
                  {isEditing
                    ? canUpdate && (
                      <button type="submit" className="productsPrimaryBtn" disabled={isSaving}>
                        {isSaving ? t("saving") : t("save_order_changes")}
                      </button>
                    )
                    : canCreate && (
                      <button
                        type="submit"
                        className="productsPrimaryBtn"
                        disabled={isSaving || categories.length === 0}
                      >
                        {isSaving ? t("saving") : t("create_order")}
                      </button>
                    )}

                  {isEditing && (
                    <button
                      type="button"
                      className="productsSecondaryBtn"
                      onClick={() => resetForm()}
                    >
                      {t("cancel")}
                    </button>
                  )}
                </div>
              </div>
            </form>

            {success && <div className="productsAlert productsAlertSuccess">{success}</div>}
            {error && <div className="productsAlert productsAlertDanger">{error}</div>}
          </div>
        </section>
      )}

      {canShowForm && creationMode === "cart" && (
        <section id="order-editor" className="productsCard productsFormCard productsCartCard">
          <div className="productsCardHeader">
            <div className="productsCardTitle">
              <h3>{editingCartOrder ? t("edit_order") : t("multi_item_order")}</h3>
              <span>
                {editingCartOrder ? t("edit_cart_order_hint") : t("multi_item_order_hint")}
              </span>
            </div>
            <div className="productsHeaderMeta">
              {editingCartOrder && (
                <span className="productsEditBadge">
                  {t("order_number", { number: editingCartOrder.ui_id || editingCartOrder.id })}
                </span>
              )}
              <button
                className="productsIconBtn"
                type="button"
                onClick={() => navigate("/menu/settings/products")}
                title={t("manage_product_catalog")}
                aria-label={t("manage_product_catalog")}
              >
                <i className="lni lni-gear-1" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <div className="productsCardBody productsCartCardBody">
            <OrderCartEditor
              key={cartEditorVersion}
              currency={currency}
              editingOrder={editingCartOrder}
              canCreate={canCreate}
              canUpdate={canUpdate}
              onDirtyChange={setCartDirty}
              onSaved={handleCartSaved}
              onCancelEdit={() => {
                setEditingCartOrder(null);
                setCartDirty(false);
                setCartEditorVersion((version) => version + 1);
              }}
            />
            {success && <div className="productsAlert productsAlertSuccess">{success}</div>}
            {error && <div className="productsAlert productsAlertDanger">{error}</div>}
          </div>
        </section>
      )}

      <section className="productsCard">
        <div className="productsCardHeader">
          <div className="productsCardTitle">
            <h3>{t("order_register")}</h3>
            <span>{t("order_register_hint")}</span>
          </div>

          <div className="productsToolbar">
            <select
              title={t("entries_per_page")}
              value={productsPerPage}
              onChange={handleProductsPerPageChange}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>

            <button
              className="productsSecondaryBtn productsRefreshBtn"
              type="button"
              onClick={fetchProducts}
              disabled={isLoading}
            >
              <i className={`lni lni-refresh-circle-1-clockwise ${isLoading ? "isSpinning" : ""}`} aria-hidden="true"></i>
              {t("refresh")}
            </button>
          </div>
        </div>

        <div className="productsCardBody">
          <div className="productsSearchRow">
            <div className="productsField productsSearchInput">
              <label>{t("search")}</label>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t("search_placeholder")}
              />
            </div>
            <div className="productsStatusFilters" aria-label={t("filter_by_status")}>
              {["all", "pending", "completed", "canceled"].map((status) => (
                <button
                  key={status}
                  type="button"
                  className={statusFilter === status ? "isActive" : ""}
                  onClick={() => handleStatusFilterChange(status)}
                >
                  {status === "all" ? t("all_orders") : t(status)}
                  <span>
                    {status === "all" ? orderStats.total : orderStats[status]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="productsLoadingState">
              <span className="productsLoader" aria-hidden="true"></span>
              <strong>{t("loading_orders")}</strong>
              <small>{t("loading_orders_hint")}</small>
            </div>
          ) : filteredProducts.length > 0 ? (
            <>
              <div className="productsMobileCards">
                {currentProducts.map((product) => (
                  <article key={product.id || product.ui_id} className="productsMobileCard">
                    <div className="productsMobileCardTop">
                      <div className="productsMobileCardTitle">
                        <strong>{product.customer_name}</strong>
                        <span>
                          #{product.ui_id || product.id} ·{" "}
                          {Number(product.item_count || 1) > 1
                            ? t("order_positions_short", { count: product.item_count })
                            : t(product.category_obj?.name || "", {
                                defaultValue:
                                  product.category_obj?.fallback_name ||
                                  product.category_obj?.name ||
                                  t("n_a"),
                              })}
                        </span>
                      </div>

                      <span className={`productsStatus productsStatus-${product.status}`}>
                        {t(product.status)}
                      </span>
                    </div>

                    <div className="productsMobileMeta">
                      <div className="productsMobileMetaItem">
                        <label>{t("count")}</label>
                        <strong>{product.count}</strong>
                      </div>

                      <div className="productsMobileMetaItem">
                          <label>{t("sub_category")}</label>
                          <strong>
                            {Number(product.item_count || 1) > 1
                              ? t("order_positions_short", { count: product.item_count })
                              : t(product.sub_category_obj?.name || "", {
                                  defaultValue:
                                    product.sub_category_obj?.fallback_name ||
                                    product.sub_category_obj?.name ||
                                    t("n_a"),
                                })}
                          </strong>
                      </div>

                      {(isWarehousePreset || categoryLooksLikeWarehouse(product.category_obj)) && (
                        <div className="productsMobileMetaItem">
                          <label>{t("size")}</label>
                          <strong>
                            {product.length} × {product.width} × {product.height}
                          </strong>
                        </div>
                      )}

                      <div className="productsMobileMetaItem">
                        <label>{t("total_price")}</label>
                        <strong>{formatMoney(product.total_price)}</strong>
                      </div>

                      {product.origin === "velshop" && (
                        <div className="productsMobileMetaItem">
                          <label>{t("payment")}</label>
                          <strong className={`commerceStatus commerceStatus-${String(product.payment_status || "pending").toLowerCase()}`}>
                            {t(`payment_${String(product.payment_status || "pending").toLowerCase()}`)}
                          </strong>
                        </div>
                      )}

                      <div className="productsMobileMetaItem">
                        <label>{t("created_at")}</label>
                        <strong>{formatDate(product.created_at)}</strong>
                      </div>

                      {tableProductCustomFields.map((field) => (
                        <div
                          key={`${product.id}-${field.key}`}
                          className="productsMobileMetaItem"
                        >
                          <label>{getCustomFieldLabel(field)}</label>
                          <strong>{getCustomFieldDisplayValue(product, field)}</strong>
                        </div>
                      ))}
                    </div>

                    {product.description && (
                      <div className="productsMobileDescription">
                        {product.description}
                      </div>
                    )}

                    <div className="productsMobileActions">
                      {product.origin === "velshop" && (
                        <button className="productsSecondaryBtn" type="button" onClick={() => setSelectedCommerceOrder(product)}>
                          {t("payment_details")}
                        </button>
                      )}
                      {canUpdate && (
                        <button
                          className="productsSecondaryBtn"
                          type="button"
                          onClick={() => openOrderForEdit(product)}
                        >
                          {t("edit")}
                        </button>
                      )}

                      {canDelete && (
                        <button
                          className="productsDangerBtn"
                          type="button"
                          onClick={() => handleDeleteOrder(product)}
                        >
                          {t("delete")}
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>

              <div className="productsDesktopTable">
                <table className="table table-striped table-hover mb-0">
                  <thead className="thead-dark">
                    <tr>
                      <th onClick={() => sortData("product_id")}>
                        {t("order")}{sortArrow("product_id")}
                      </th>
                      <th onClick={() => sortData("customer_name")}>
                        {t("customer")}{sortArrow("customer_name")}
                      </th>
                      <th onClick={() => sortData("sub_category")}>
                        {t("order_item")}{sortArrow("sub_category")}
                      </th>
                      <th onClick={() => sortData("count")}>
                        {t("quantity")}{sortArrow("count")}
                      </th>
                      <th onClick={() => sortData("total_price")}>
                        {t("total_price")}{sortArrow("total_price")}
                      </th>
                      <th>{t("payment")}</th>
                      <th>{t("delivery")}</th>
                      <th onClick={() => sortData("status")}>
                        {t("status")}{sortArrow("status")}
                      </th>
                      <th onClick={() => sortData("created_at")}>
                        {t("created_at")}{sortArrow("created_at")}
                      </th>

                      {tableProductCustomFields.map((field) => (
                        <th key={field.key}>{getCustomFieldLabel(field)}</th>
                      ))}

                      <th>{t("actions")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentProducts.map((product) => (
                      <tr key={product.id || product.ui_id}>
                        <td>
                          <div className="productsOrderIdCell">
                            <strong>#{product.ui_id || product.id}</strong>
                            <span>{t("updated_short")}: {formatDate(product.last_update)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="productsCustomerCell">
                            <strong>{product.customer_name}</strong>
                            <span title={product.description || ""}>
                              {product.description || t("no_order_note")}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="productsItemCell">
                            {(product.items || [product]).slice(0, 3).map((item, itemIndex) => (
                              <div className="productsItemLine" key={item.id || `${product.id}-${itemIndex}`}>
                                <strong>{getSubcategoryDisplayName(item)}</strong>
                                <span>
                                  {getCategoryDisplayName(item)} · {t("quantity_short", { count: item.count })}
                                </span>
                              </div>
                            ))}
                            {Number(product.item_count || 1) > 3 && (
                              <small className="productsMoreItems">
                                {t("more_order_items", { count: product.item_count - 3 })}
                              </small>
                            )}
                          </div>
                        </td>
                        <td><strong>{product.count}</strong></td>
                        <td className="productsMoneyCell">{formatMoney(product.total_price)}</td>
                        <td>
                          {product.origin === "velshop" ? (
                            <div className="commercePaymentCell">
                              <span className={`commerceStatus commerceStatus-${String(product.payment_status || "pending").toLowerCase()}`}>
                                {t(`payment_${String(product.payment_status || "pending").toLowerCase()}`)}
                              </span>
                              <small>{product.payment_provider || product.payment_method || "—"}</small>
                            </div>
                          ) : <span className="commerceMuted">—</span>}
                        </td>
                        <td>
                          {product.origin === "velshop" ? (
                            <div className="commerceDeliveryCell">
                              <strong>{t(`delivery_${product.delivery_method || "unknown"}`, { defaultValue: product.delivery_method || "—" })}</strong>
                              <small>{Number(product.delivery_fee || 0) > 0 ? formatMoney(product.delivery_fee) : t("free")}</small>
                            </div>
                          ) : <span className="commerceMuted">—</span>}
                        </td>
                        <td>
                          <span className={`productsStatus productsStatus-${product.status}`}>
                            {t(product.status)}
                          </span>
                        </td>
                        <td className="productsDateCell">{formatDate(product.created_at)}</td>

                        {tableProductCustomFields.map((field) => (
                          <td key={`${product.id}-${field.key}`}>
                            {getCustomFieldDisplayValue(product, field)}
                          </td>
                        ))}

                        <td>
                          <div className="productsTableActions">
                            {product.origin === "velshop" && (
                              <button className="productsSmallInfoBtn" type="button" onClick={() => setSelectedCommerceOrder(product)}>
                                {t("details")}
                              </button>
                            )}
                            {canUpdate && (
                              <button
                                className="productsSmallWarningBtn"
                                type="button"
                                onClick={() => openOrderForEdit(product)}
                              >
                                {t("edit")}
                              </button>
                            )}

                            {canDelete && (
                              <button
                                className="productsSmallDangerBtn"
                                type="button"
                                onClick={() => handleDeleteOrder(product)}
                              >
                                {t("delete")}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="productsListFooter">
                <div className="productsShowingText">{showingText}</div>

                <nav aria-label={t("page_navigation")}>
                  <ul className="pagination mb-0 productsPagination">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        type="button"
                        title={t("previous")}
                        aria-label={t("previous")}
                        onClick={() => paginate(currentPage - 1)}
                      >
                        <i className="lni lni-arrow-left"></i>
                      </button>
                    </li>

                    {Array.from({ length: totalPages }, (_, idx) => (
                      <li
                        key={idx}
                        className={`page-item ${currentPage === idx + 1 ? "active" : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          type="button"
                          onClick={() => paginate(idx + 1)}
                        >
                          {idx + 1}
                        </button>
                      </li>
                    ))}

                    <li
                      className={`page-item ${currentPage === totalPages || totalPages === 0
                        ? "disabled"
                        : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        type="button"
                        title={t("next")}
                        aria-label={t("next")}
                        onClick={() => paginate(currentPage + 1)}
                      >
                        <i className="lni lni-arrow-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          ) : (
            <div className="productsEmptyState productsOrdersEmptyState">
              <span className="productsEmptyIcon">
                <i className="lni lni-clipboard" aria-hidden="true"></i>
              </span>
              <strong>
                {searchQuery || statusFilter !== "all" || originFilter !== "all" || paymentFilter !== "all"
                  ? t("no_orders_match_filters")
                  : t("no_orders_yet")}
              </strong>
              <p>
                {searchQuery || statusFilter !== "all" || originFilter !== "all" || paymentFilter !== "all"
                  ? t("no_orders_match_filters_hint")
                  : t("no_orders_yet_hint")}
              </p>
              <div className="productsEmptyActions">
                {(searchQuery || statusFilter !== "all" || originFilter !== "all" || paymentFilter !== "all") && (
                  <button
                    type="button"
                    className="productsSecondaryBtn"
                    onClick={() => {
                      setSearchQuery("");
                      setOriginFilter("all");
                      setPaymentFilter("all");
                      handleStatusFilterChange("all");
                    }}
                  >
                    {t("clear_filters")}
                  </button>
                )}
                {canCreate && (
                  <button type="button" className="productsPrimaryBtn" onClick={startNewOrder}>
                    {t("create_first_order")}
                  </button>
                )}
                <button
                  type="button"
                  className="productsSecondaryBtn"
                  onClick={() => navigate("/menu/settings/products")}
                >
                  {t("manage_product_catalog")}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {selectedCommerceOrder && (
        <div className="commerceModalBackdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedCommerceOrder(null); }}>
          <section className="commerceModal" role="dialog" aria-modal="true" aria-labelledby="commerce-modal-title">
            <header className="commerceModalHeader">
              <div>
                <span>{t("online_order")}</span>
                <h3 id="commerce-modal-title">#{selectedCommerceOrder.ui_id || selectedCommerceOrder.id} · {selectedCommerceOrder.customer_name}</h3>
              </div>
              <button type="button" className="commerceCloseBtn" onClick={() => setSelectedCommerceOrder(null)} aria-label={t("close")}>×</button>
            </header>

            <div className="commerceModalBody">
              <div className="commerceSummaryGrid">
                <div><span>{t("amount")}</span><strong>{formatMoney(selectedCommerceOrder.total_price)}</strong></div>
                <div><span>{t("payment_status")}</span><strong>{t(`payment_${String(selectedCommerceOrder.payment_status || "pending").toLowerCase()}`)}</strong></div>
                <div><span>{t("payment_method")}</span><strong>{selectedCommerceOrder.payment_method || "—"}</strong></div>
                <div><span>{t("payment_provider")}</span><strong>{selectedCommerceOrder.payment_provider || "—"}</strong></div>
                <div><span>{t("provider_id")}</span><strong className="commerceMono">{selectedCommerceOrder.payment_provider_id || "—"}</strong></div>
                <div><span>{t("delivery")}</span><strong>{selectedCommerceOrder.delivery_method || "—"}</strong></div>
                <div><span>{t("delivery_fee")}</span><strong>{formatMoney(selectedCommerceOrder.delivery_fee)}</strong></div>
                <div><span>{t("discount")}</span><strong>{formatMoney(selectedCommerceOrder.discount_amount)}</strong></div>
              </div>

              <div className="commerceInfoPanel">
                <h4>{t("fulfillment_details")}</h4>
                <p>{selectedCommerceOrder.pickup_location || selectedCommerceOrder.delivery_address || t("no_delivery_address")}</p>
                {selectedCommerceOrder.pickup_address && <small>{selectedCommerceOrder.pickup_address}</small>}
              </div>

              <div className="commerceWorkflow">
                <div className="commerceWorkflowBlock">
                  <label>{t("payment_status")}</label>
                  <select
                    value={String(selectedCommerceOrder.payment_status || "PENDING").toUpperCase()}
                    disabled={commerceSaving || !canUpdate}
                    onChange={(event) => updateCommerceState(selectedCommerceOrder, { payment_status: event.target.value })}
                  >
                    {['PENDING','PAID','FAILED','CANCELED','REFUNDED','UNKNOWN'].map((value) => (
                      <option key={value} value={value}>{t(`payment_${value.toLowerCase()}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="commerceWorkflowBlock">
                  <label>{t("refund_status")}</label>
                  <select
                    value={String(selectedCommerceOrder.refund_status || "NONE").toUpperCase()}
                    disabled={commerceSaving || !canUpdate}
                    onChange={(event) => updateCommerceState(selectedCommerceOrder, { refund_status: event.target.value })}
                  >
                    {['NONE','REQUESTED','PROCESSING','REFUNDED','REJECTED'].map((value) => (
                      <option key={value} value={value}>{t(`refund_${value.toLowerCase()}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="commerceWorkflowBlock">
                  <label>{t("cancellation_status")}</label>
                  <select
                    value={String(selectedCommerceOrder.cancellation_status || "NONE").toUpperCase()}
                    disabled={commerceSaving || !canUpdate}
                    onChange={(event) => updateCommerceState(selectedCommerceOrder, { cancellation_status: event.target.value })}
                  >
                    {['NONE','REQUESTED','APPROVED','REJECTED'].map((value) => (
                      <option key={value} value={value}>{t(`cancellation_${value.toLowerCase()}`)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="commerceInfoPanel">
                <h4>{t("commerce_note")}</h4>
                <textarea
                  key={`${selectedCommerceOrder.id}-${selectedCommerceOrder.commerce_note || ''}`}
                  defaultValue={selectedCommerceOrder.commerce_note || ''}
                  placeholder={t("commerce_note_placeholder")}
                  onBlur={(event) => {
                    const nextValue = event.target.value.trim();
                    if (nextValue !== String(selectedCommerceOrder.commerce_note || '')) {
                      updateCommerceState(selectedCommerceOrder, { note: nextValue });
                    }
                  }}
                  disabled={commerceSaving || !canUpdate}
                />
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default Products;
